import { useEffect, useRef, useState } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Grid } from '@material-ui/core';

const DATADOG_ANNOTATION_GRAPH_TOKEN = 'datadoghq.com/graph-token';
const DATADOG_ANNOTATION_GRAPH_SIZE = 'datadoghq.com/graph-size';
const DATADOG_ANNOTATION_GRAPH_TITLE = 'datadoghq.com/graph-title';
const DATADOG_ANNOTATION_SITE = 'datadoghq.com/site';

const graphSizeToDimensions = (graphSize: string) => {
  switch (graphSize) {
    case 'small':
      return { width: 400, height: 200 };
    case 'large':
      return { width: 800, height: 400 };
    case 'x-large':
      return { width: 1000, height: 500 };
    case 'medium':
    default:
      return { width: 600, height: 300 };
  }
};

const useContainerWidth = (fallback: number) => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(fallback);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(([entry]) => {
      const measured = Math.round(entry.contentRect.width / 20) * 20;
      if (measured > 0) {
        setWidth(measured);
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { ref, width };
};

const DatadogGraph = ({
  token,
  title,
  site,
  height,
  fallbackWidth,
  index,
}: {
  token: string;
  title: string;
  site: string;
  height: number;
  fallbackWidth: number;
  index: number;
}) => {
  const { ref, width } = useContainerWidth(fallbackWidth);

  return (
    <InfoCard title={title || 'Datadog Graph'}>
      <div ref={ref}>
        <iframe
          title={title || `Datadog graph ${index}`}
          data-testid={`Datadog graph ${index}`}
          src={`https://app.${site}/graph/embed?token=${token}&height=${height}&width=${width}&legend=true`}
          height={height}
          frameBorder="0"
          style={{ width: '100%', border: 0, display: 'block' }}
        />
      </div>
    </InfoCard>
  );
};

export const DatadogGraphCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations ?? {};
  const tokens = (annotations[DATADOG_ANNOTATION_GRAPH_TOKEN] ?? '')
    .split(',')
    .map(token => token.trim())
    .filter(Boolean);

  if (tokens.length === 0) {
    return null;
  }

  const site = annotations[DATADOG_ANNOTATION_SITE] ?? 'datadoghq.eu';
  const titles = (annotations[DATADOG_ANNOTATION_GRAPH_TITLE] ?? '')
    .split(',')
    .map(title => title.trim());
  const { width, height } = graphSizeToDimensions(
    annotations[DATADOG_ANNOTATION_GRAPH_SIZE] ?? 'medium',
  );

  return (
    <Grid container spacing={3}>
      {tokens.map((token, index) => (
        <Grid item xs={12} md={4} key={token}>
          <DatadogGraph
            token={token}
            title={titles[index]}
            site={site}
            height={height}
            fallbackWidth={width}
            index={index}
          />
        </Grid>
      ))}
    </Grid>
  );
};
